import React, { useEffect, useRef } from 'react';
import { logger } from '../utils/logger';
import { isAdsEnabled, isDevEnvironment } from '../utils/adConfig';

interface AdSenseProps {
  client?: string;
  slot: string;
  format?: string;
  style?: React.CSSProperties;
  className?: string;
  responsive?: boolean;
  layoutKey?: string;
}

const AdSense: React.FC<AdSenseProps> = ({
  client = 'ca-pub-2829473084602625',
  slot,
  format = 'auto',
  style = { display: 'block' },
  className = '',
  responsive = true,
  layoutKey,
}) => {
  const insRef = useRef<HTMLModElement | null>(null);
  const adsEnabled = isAdsEnabled();

  useEffect(() => {
    if (!adsEnabled) {
      return;
    }

    try {
      if (typeof window === 'undefined') {
        return;
      }

      // Inicializar el array si no existe
      window.adsbygoogle = window.adsbygoogle || [];

      // En React 18 StrictMode el efecto puede ejecutarse 2 veces en dev.
      // Si el anuncio ya fue procesado por AdSense, evitamos un push duplicado.
      if (insRef.current?.getAttribute('data-adsbygoogle-status') === 'done') {
        return;
      }
      
      // Push el anuncio al array
      window.adsbygoogle.push({});
    } catch (error) {
      logger.error('Error loading AdSense:', error);
    }
  }, [adsEnabled]);

  // En desarrollo/local: mostrar placeholder visual en vez del anuncio real
  if (!adsEnabled) {
    if (!isDevEnvironment()) return null;
    return (
      <div
        className={className}
        style={{
          ...style,
          background: 'repeating-linear-gradient(45deg, #f0f0f0, #f0f0f0 10px, #e0e0e0 10px, #e0e0e0 20px)',
          border: '2px dashed #ccc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50px',
          color: '#999',
          fontSize: '12px',
          fontFamily: 'monospace',
        }}
      >
        [AD] slot: {slot} (deshabilitado en dev)
      </div>
    );
  }

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={responsive ? 'true' : 'false'}
      {...(layoutKey && { 'data-ad-layout-key': layoutKey })}
    />
  );
};

export default AdSense;

